import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Link } from "react-router";
import { ArrowDown, ArrowUp, Plus, Undo2 } from "lucide-react";

import {
  getBacklogMeta,
  handToAgent,
  listAllBacklogItems,
  recallFromAgent,
  updateBacklogItem,
} from "@/api/backlog";
import { Checkbox, Field, Input, Select } from "@/components/form";
import { ErrorBox, MutationError, RefreshFailed, loadFailure } from "@/components/Spinner";
import type { BacklogItem, UpdateBacklogItemInput } from "@/types/api";
import {
  Button,
  ButtonLink,
  EmptyState,
  IconButton,
  Page,
  PageHeader,
  Table,
  type TableColumn,
  type Tone,
} from "@/components/ui";
import { formatDate } from "@/lib/dates";

/**
 * Backlog — the org GitHub Project rendered as a table.
 *
 * Ordering deserves a note. The backlog's fine-grained ranking IS the
 * Project's own item order: the API returns items in that order and
 * `updateProjectV2ItemPosition` is the only way to change it. There is no
 * rank column to sort on, so the reorder arrows are meaningful only while the
 * table is showing that native order — sorting by any column disables them
 * rather than silently reordering something the user cannot see.
 */

type SortKey = "project" | "priority" | "area" | "repository" | "updated";

const ALL = "__all__";

// Les valeurs viennent du Project GitHub : une valeur inconnue reste neutre.
const PRIORITY_TONES: Record<string, Tone> = {
  P0: "danger",
  P1: "warning",
  P2: "neutral",
  P3: "neutral",
};

const STATUS_TONES: Record<string, Tone> = {
  Backlog: "neutral",
  Next: "info",
  "In progress": "warning",
  Blocked: "danger",
  Done: "success",
};

function shortRepo(nameWithOwner: string): string {
  return nameWithOwner.replace(/^.*\//, "").replace(/^pulsestation-/, "");
}

/**
 * A select that writes one Project field on change. `label` names it for
 * assistive technology ("Status of <item>"): the row is the only context a
 * sighted reader has, and a screen reader does not get it.
 */
function FieldSelect({
  label,
  value,
  options,
  tones,
  disabled,
  onChange,
}: {
  label: string;
  value: string | null;
  options: string[];
  tones?: Record<string, Tone>;
  disabled: boolean;
  onChange: (next: string) => void;
}) {
  return (
    <Select
      variant="pill"
      aria-label={label}
      tone={(value && tones?.[value]) || "neutral"}
      value={value ?? ""}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      options={[
        ...(value === null ? [{ value: "", label: "—" }] : []),
        ...options.map((option) => ({ value: option, label: option })),
      ]}
    />
  );
}

/**
 * Hand an item to the coding agent, or take it back.
 *
 * Deliberately a button and not a toggle in the row's select group: this is the
 * one control on this page that does something outside the control plane — it
 * starts an agent that will open a pull request. The reasons it can be
 * unavailable are spelled out in the tooltip rather than left to guesswork.
 */
function AgentButton({
  item,
  label,
  supported,
  busy,
  onToggle,
}: {
  item: BacklogItem;
  label: string;
  supported: boolean;
  busy: boolean;
  onToggle: (hand: boolean) => void;
}) {
  const handed = item.labels.includes(label);
  const closed = item.state !== "open";

  // Recalling stays available on an unsupported repo: the label may have been
  // put there by hand, and refusing to remove it would strand the item.
  const blocked = closed || (!handed && !supported);
  const title = handed
    ? "Remove the label: the agent won't pick up this item"
    : closed
      ? "The issue is closed"
      : supported
        ? "Add the ready-for-claude label: the agent will open a pull request"
        : "No agent runs in this repo";

  return (
    <Button
      variant={handed ? "secondary" : "ghost"}
      size="sm"
      icon={handed ? Undo2 : undefined}
      disabled={busy || blocked}
      title={title}
      onClick={() => onToggle(!handed)}
    >
      {handed ? "Recall" : "Hand off"}
    </Button>
  );
}

export function BacklogPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const queryClient = useQueryClient();

  const [sort, setSort] = useState<SortKey>("project");
  const [repo, setRepo] = useState(ALL);
  const [area, setArea] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [priority, setPriority] = useState(ALL);
  const [showClosed, setShowClosed] = useState(false);
  const [search, setSearch] = useState("");

  const metaQuery = useQuery({
    queryKey: ["backlog", "meta"],
    queryFn: () => getBacklogMeta(token),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  const itemsQuery = useQuery({
    queryKey: ["backlog", "items"],
    queryFn: () => listAllBacklogItems(token),
    enabled: !!token,
    staleTime: 30 * 1000,
  });

  const mutation = useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: UpdateBacklogItemInput }) =>
      updateBacklogItem(token, itemId, input),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["backlog", "items"] }),
  });

  /**
   * Hand an item over to the agent, or take it back.
   *
   * Separate from `mutation` so the table's selects stay usable while a
   * hand-off is in flight, and so a failure here (a closed issue, a repo with
   * no agent) doesn't read as a failed priority edit.
   */
  const agentMutation = useMutation({
    mutationFn: ({ itemId, hand }: { itemId: string; hand: boolean }) =>
      hand ? handToAgent(token, itemId) : recallFromAgent(token, itemId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["backlog", "items"] }),
  });

  const agentRepos = metaQuery.data?.agentRepos ?? [];
  const agentLabel = metaQuery.data?.agentLabel ?? "ready-for-claude";

  const all = itemsQuery.data ?? [];

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = all.filter(
      (item) =>
        (showClosed || item.state === "open") &&
        (repo === ALL || item.repository === repo) &&
        (area === ALL || item.area === area) &&
        (status === ALL || item.status === status) &&
        (priority === ALL || item.priority === priority) &&
        (!needle || item.title.toLowerCase().includes(needle)),
    );
    if (sort === "project") return filtered;
    const key = (item: BacklogItem): string => {
      if (sort === "priority") return item.priority ?? "zz";
      if (sort === "area") return item.area ?? "zz";
      if (sort === "repository") return item.repository;
      return item.updatedAt;
    };
    const direction = sort === "updated" ? -1 : 1;
    return [...filtered].sort((a, b) => key(a).localeCompare(key(b)) * direction);
  }, [all, repo, area, status, priority, showClosed, search, sort]);

  // Reordering is expressed against the FULL project order, not the filtered
  // view: "one row up" in a filtered table would otherwise land the item in an
  // unpredictable spot among the rows that are hidden.
  const move = (item: BacklogItem, direction: -1 | 1) => {
    const index = all.findIndex((candidate) => candidate.itemId === item.itemId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= all.length) return;
    const afterItemId =
      direction === -1
        ? index - 2 >= 0
          ? all[index - 2].itemId
          : null
        : all[index + 1].itemId;
    mutation.mutate({ itemId: item.itemId, input: { afterItemId } });
  };

  const fields = metaQuery.data?.fields ?? {};
  const areas = fields.Area ?? [];
  const priorities = fields.Priority ?? [];
  const statuses = fields.Status ?? [];
  const repos = metaQuery.data?.repos ?? [];
  const reorderable = sort === "project";

  const columns: TableColumn<BacklogItem>[] = [
    {
      key: "order",
      header: "Order",
      cell: (item) => (
        <div className="flex gap-0.5">
          <IconButton
            icon={ArrowUp}
            label={`Move ${item.title} up`}
            size="sm"
            title={reorderable ? "Move up" : "Reordering requires Project order"}
            disabled={!reorderable || mutation.isPending}
            onClick={() => move(item, -1)}
          />
          <IconButton
            icon={ArrowDown}
            label={`Move ${item.title} down`}
            size="sm"
            title={reorderable ? "Move down" : "Reordering requires Project order"}
            disabled={!reorderable || mutation.isPending}
            onClick={() => move(item, 1)}
          />
        </div>
      ),
    },
    {
      key: "title",
      header: "Title",
      cell: (item) => (
        <>
          <Link to={`/backlog/${encodeURIComponent(item.itemId)}/edit`} className="text-brand-600 hover:underline">
            {item.title}
          </Link>
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className="ml-2 font-mono text-xs text-ink-muted hover:underline"
          >
            #{item.number}
          </a>
        </>
      ),
    },
    { key: "repo", header: "Repo", mono: true, cell: (item) => shortRepo(item.repository) },
    {
      key: "area",
      header: "Area",
      cell: (item) => (
        <FieldSelect
          label={`Area of ${item.title}`}
          value={item.area}
          options={areas}
          disabled={mutation.isPending}
          onChange={(next) => mutation.mutate({ itemId: item.itemId, input: { area: next } })}
        />
      ),
    },
    {
      key: "priority",
      header: "Priority",
      cell: (item) => (
        <FieldSelect
          label={`Priority of ${item.title}`}
          value={item.priority}
          options={priorities}
          tones={PRIORITY_TONES}
          disabled={mutation.isPending}
          onChange={(next) => mutation.mutate({ itemId: item.itemId, input: { priority: next } })}
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (item) => (
        <FieldSelect
          label={`Status of ${item.title}`}
          value={item.status}
          options={statuses}
          tones={STATUS_TONES}
          disabled={mutation.isPending}
          onChange={(next) => mutation.mutate({ itemId: item.itemId, input: { status: next } })}
        />
      ),
    },
    {
      key: "agent",
      header: "Agent",
      cell: (item) => (
        <AgentButton
          item={item}
          label={agentLabel}
          supported={agentRepos.includes(item.repository)}
          busy={agentMutation.isPending}
          onToggle={(hand) => agentMutation.mutate({ itemId: item.itemId, hand })}
        />
      ),
    },
    { key: "updated", header: "Updated", mono: true, muted: true, cell: (item) => formatDate(item.updatedAt) },
  ];

  return (
    <Page>
      <PageHeader
        title="Backlog"
        subtitle={
          <>
            An item = a GitHub issue, gathered in the org's Project. Fine-grained
            priority is the Project's order; the arrows are only active on
            that order.{" "}
            {metaQuery.data && (
              <a
                href={metaQuery.data.projectUrl}
                target="_blank"
                rel="noreferrer"
                className="text-brand-600 hover:underline"
              >
                Open on GitHub
              </a>
            )}
          </>
        }
        actions={
          <ButtonLink to="/backlog/new" variant="primary" icon={Plus}>
            New item
          </ButtonLink>
        }
      />

      <div className="card mb-4 flex flex-wrap items-center gap-2 p-3">
        <Field label="Filter by title" hideLabel>
          <Input
            size="sm"
            placeholder="Filter by title…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Field>
        <Field label="Repository" hideLabel>
          <Select
            size="sm"
            value={repo}
            onChange={(event) => setRepo(event.target.value)}
            options={[
              { value: ALL, label: "All repos" },
              ...repos.map((value) => ({ value, label: shortRepo(value) })),
            ]}
          />
        </Field>
        <Field label="Area" hideLabel>
          <Select
            size="sm"
            value={area}
            onChange={(event) => setArea(event.target.value)}
            options={[
              { value: ALL, label: "All areas" },
              ...areas.map((value) => ({ value, label: value })),
            ]}
          />
        </Field>
        <Field label="Priority" hideLabel>
          <Select
            size="sm"
            value={priority}
            onChange={(event) => setPriority(event.target.value)}
            options={[
              { value: ALL, label: "All priorities" },
              ...priorities.map((value) => ({ value, label: value })),
            ]}
          />
        </Field>
        <Field label="Status" hideLabel>
          <Select
            size="sm"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            options={[
              { value: ALL, label: "All statuses" },
              ...statuses.map((value) => ({ value, label: value })),
            ]}
          />
        </Field>
        <Field label="Sort" hideLabel>
          <Select
            size="sm"
            value={sort}
            onChange={(event) => setSort(event.target.value as SortKey)}
            options={[
              { value: "project", label: "Project order" },
              { value: "priority", label: "Sort by priority" },
              { value: "area", label: "Sort by area" },
              { value: "repository", label: "Sort by repo" },
              { value: "updated", label: "Sort by date" },
            ]}
          />
        </Field>
        <Checkbox
          label="Include closed items"
          checked={showClosed}
          onChange={(event) => setShowClosed(event.target.checked)}
        />
        <span className="ml-auto text-sm text-ink-muted">
          {visible.length} / {all.length}
        </span>
      </div>

      {metaQuery.isError && <ErrorBox error={metaQuery.error} />}
      {/* One box per action, each says which action failed (the edit of a field, or the hand-off to the agent). */}
      <MutationError mutation={mutation} title="Could not update the item" className="mb-4" />
      <MutationError
        mutation={agentMutation}
        title={
          agentMutation.variables?.hand === false
            ? "Could not take the item back from the agent"
            : "Could not hand the item to the agent"
        }
        className="mb-4"
      />
      <RefreshFailed query={itemsQuery} className="mb-4" />

      <Table
        label="Backlog items"
        columns={columns}
        rows={itemsQuery.data ? visible : undefined}
        rowKey={(item) => item.itemId}
        error={loadFailure(itemsQuery)}
        rowEmphasis={(item) => (item.state === "closed" ? "muted" : undefined)}
        empty={
          all.length === 0 ? (
            <EmptyState title="No backlog items yet">
              Items are the issues gathered in the org&apos;s Project.
            </EmptyState>
          ) : (
            <EmptyState title="No item matches these filters" />
          )
        }
      />
    </Page>
  );
}
